import { MapThumbnail, MapTypes, useHasMap } from '@/components/maps/mapThumbnail';
import { MapWidget } from '@/components/maps/mapWidget';
import { ArticleContainer } from '@/routes/resource/key/components/articleContainer';
import { ArticleTextContainer } from '@/routes/resource/key/components/articleTextContainer';
import { useContext, useState, useCallback } from 'react';
import { InvasiveInCountries } from './sections/InvasiveInCountries';
import { TaxonKeyContext } from './taxonKeyPresentation';
import { useIsFamilyOrAbove, useIsSpeciesOrBelow } from '@/hooks/taxonomyRankHooks';
import TreatmentsCard from './sections/Treatments';
import TypeMaterial from './sections/TypeSpecimens';
import { NotFoundError } from '@/errors';
import { useConfig } from '@/config/config';
import BibliographyCard from './sections/BibliographyCard';
import CitationCard from './sections/CitationCard';
import ClassificationCard from './sections/ClassificationCard';
import OccurrenceMediaGalleryCard from './sections/OccurrenceMediaGalleryCard';
import SynonymsCard from './sections/SynonymsCard';
import TaxonIdentifiersCard from './sections/TaxonIdentifiersCard';
import VernacularNamesCard from './sections/VernacularNamesCard';
import { CatalogueOfLifeCard, TocLi as Li, Separator } from '@/components/TocHelp';
import { Card } from '@/components/ui/largeCard';
import useBelow from '@/hooks/useBelow';
import { FormattedMessage } from 'react-intl';
import { Aside, AsideSticky, SidebarLayout } from '../../occurrence/key/pagelayouts';
import { BreakdownCard, useTaxonBreakdown } from './sections/breakdown';
import { ErrorMessage } from '@/components/errorMessage';

export default function AboutBackbone() {
  const config = useConfig();
  const { slowTaxon, data } = useContext(TaxonKeyContext);
  const hideSidebar = useBelow(1000);
  const [hasTypeMaterial, setHasTypeMaterial] = useState(false);
  const onTypeMaterialData = useCallback((hasData: boolean) => setHasTypeMaterial(hasData), []);

  const taxonInfo = data?.taxonInfo;
  if (!taxonInfo) throw new NotFoundError();
  const taxon = taxonInfo;

  const isFamilyOrAbove = useIsFamilyOrAbove(taxon?.taxonRank);
  const isSpeciesOrBelow = useIsSpeciesOrBelow(taxon?.taxonRank);

  const hasSynonyms =
    (taxonInfo.synonyms?.homotypic?.length ?? 0) > 0 ||
    (taxonInfo.synonyms?.heterotypic?.flat().length ?? 0) > 0;

  const { hasMap: hasPreprocessedMap, error: mapError } = useHasMap({
    [MapTypes.TaxonKey]: taxon?.taxonID,
    checklistKey: config.defaultChecklistKey,
  });

  const showTaxonBreakdown = isFamilyOrAbove && taxon.taxonomicStatus === 'ACCEPTED';
  const { hasData: hasBreakdownData } = useTaxonBreakdown({
    taxonKey: taxon.taxonID,
    datasetKey: taxon.datasetKey,
  });
  const showSynonyms = taxon.taxonomicStatus === 'ACCEPTED' && hasSynonyms;
  const hasVernacularNames = (taxonInfo?.vernacularNames?.length ?? 0) > 0;
  const hasTreatments = (taxonInfo?.treatments?.length ?? 0) > 0;
  const hasOccurrenceImages = !!(
    taxon?.occurrenceMedia?.results && (taxon?.occurrenceMedia?.count ?? 0) > 0
  );
  const hasBibliography = (taxonInfo?.bibliography?.length ?? 0) > 0;
  const hasInvasiveData = !!taxonInfo?.relatedInfo?.griis?.length;
  const hasWikiDataIdentifiers = !!(
    slowTaxon && (slowTaxon?.taxonInfo?.wikiData?.identifiers?.length ?? 0) > 0
  );

  if (!taxon) return null;
  return (
    <ArticleContainer className="g-bg-slate-100 g-pt-4">
      <ArticleTextContainer className="g-max-w-screen-xl">
        <SidebarLayout
          // reverse
          className="g-grid-cols-[250px_minmax(0,1fr)] xl:g-grid-cols-[300px_minmax(0,1fr)]"
          stack={hideSidebar}
        >
          <div className="g-order-last">
            <ClassificationCard
              datasetKey={taxon.datasetKey}
              taxonKey={taxon.taxonID}
              checklistbankURL={taxonInfo.checklistbankURL}
            />
            {showTaxonBreakdown && hasBreakdownData && (
              <div id="breakdown">
                {/* <TaxonBreakdown taxon={taxon} /> */}
                <BreakdownCard taxonKey={taxon.taxonID} datasetKey={taxon.datasetKey} />
              </div>
            )}
            {isSpeciesOrBelow && (
              <TypeMaterial taxonInfo={taxonInfo} onHasData={onTypeMaterialData} />
            )}
            {showSynonyms && <SynonymsCard taxonInfo={taxonInfo} />}
            {hasInvasiveData && <InvasiveInCountries taxonInfo={taxonInfo} />}
            {hasPreprocessedMap && (
              <div id="map">
                <Card className="g-mb-4 g-overflow-hidden">
                  <MapWidget
                    capabilitiesParams={{ taxonKey: taxon.taxonID, checklistKey: taxon.datasetKey }}
                    defaultStyleName="CLASSIC_HEX"
                    persistStyleSelection
                  />
                </Card>
              </div>
            )}
            {mapError && <ErrorMessage className="g-mb-4">Unable to load map</ErrorMessage>}
            {hasOccurrenceImages && <OccurrenceMediaGalleryCard taxon={taxon} />}
            {hasVernacularNames && <VernacularNamesCard taxonInfo={taxonInfo} />}
            {hasTreatments && <TreatmentsCard taxonInfo={taxonInfo} />}
            {hasBibliography && <BibliographyCard taxonInfo={taxonInfo} />}
            {hasWikiDataIdentifiers && <TaxonIdentifiersCard slowTaxon={slowTaxon} />}
            <CitationCard taxonInfo={taxonInfo} />
          </div>
          {!hideSidebar && (
            <Aside>
              {hasPreprocessedMap && (
                <a href="#map" className="g-block">
                  <MapThumbnail
                    blend
                    hexPerTile={48}
                    capabilitiesParams={{ taxonKey: taxon.taxonID, checklistKey: taxon.datasetKey }} // Pass taxonKey to check if there is data to show on the map for this taxon
                    overlayStyle="classic-noborder.poly"
                    className="g-mb-4 g-rounded"
                  />
                </a>
              )}
              <AsideSticky>
                <Card>
                  <nav>
                    <ul className="g-list-none g-m-0 g-p-0 g-my-2">
                      <Li to="#classification">
                        <FormattedMessage
                          id="taxon.classification"
                          defaultMessage="Classification"
                        />
                      </Li>
                      {showTaxonBreakdown && hasBreakdownData && (
                        <Li to="#breakdown">
                          <FormattedMessage
                            id="taxon.largestGroups"
                            defaultMessage="Largest groups"
                          />
                        </Li>
                      )}
                      {hasTypeMaterial && (
                        <Li to="#typeMaterial">
                          <FormattedMessage
                            id="taxon.typeMaterial"
                            defaultMessage="Type material"
                          />
                        </Li>
                      )}
                      {showSynonyms && (
                        <Li to="#synonyms">
                          <FormattedMessage id="taxon.synonyms" defaultMessage="Synonyms" />
                        </Li>
                      )}
                      {hasInvasiveData && (
                        <Li to="#invasiveInCountries">
                          <FormattedMessage id="taxon.invasive" defaultMessage="Invasive species" />
                        </Li>
                      )}
                      {hasPreprocessedMap && (
                        <Li to="#map">
                          <FormattedMessage id="taxon.map" defaultMessage="Map" />
                        </Li>
                      )}
                      {hasOccurrenceImages && (
                        <Li to="#occurrence-images">
                          <FormattedMessage id="taxon.occurrenceImages" defaultMessage="Images" />
                        </Li>
                      )}
                      {hasVernacularNames && (
                        <Li to="#vernacularNames">
                          <FormattedMessage
                            id="taxon.vernacularNames"
                            defaultMessage="Vernacular names"
                          />
                        </Li>
                      )}
                      {hasTreatments && (
                        <Li to="#treatments">
                          <FormattedMessage id="taxon.treatments" defaultMessage="Treatments" />
                        </Li>
                      )}
                      {hasBibliography && (
                        <Li to="#bibliography">
                          <FormattedMessage id="taxon.bibliography" defaultMessage="Bibliography" />
                        </Li>
                      )}
                      {hasWikiDataIdentifiers && (
                        <Li to="#taxonIdentifiers">
                          <FormattedMessage id="taxon.identifiers" defaultMessage="Identifiers" />
                        </Li>
                      )}
                      <Separator />
                      <Li to="#citation">
                        <FormattedMessage id="phrases.citation" />
                      </Li>
                    </ul>
                  </nav>
                </Card>

                <CatalogueOfLifeCard
                  datasetKey={taxon.datasetKey}
                  taxonID={taxon.taxonID}
                  checklistbankURL={taxonInfo?.checklistbankURL}
                  className="g-mt-4"
                />
                {/* Linking to gbif.org makes little sense currently as the taxonomies differ */}
                {/* {config.linkToGbifOrg && (
                  <GbifLinkCard className="g-mt-4" path={`/species/${taxon.taxonID}`} />
                )} */}
              </AsideSticky>
            </Aside>
          )}
        </SidebarLayout>
      </ArticleTextContainer>
    </ArticleContainer>
  );
}
